/**
 * Data layer for `affiliate-groups`. Thin typed fetch wrappers over
 * /api/v1/affiliate-groups. Raw `fetch()` is confined to this file
 * per the dash contract (D6).
 */

export interface AffiliateGroupRow {
	id: string;
	tenant_id: string;
	name: string;
	color: string | null;
	description: string | null;
	member_count: number;
	created_at: number;
	updated_at: number;
}

export interface ListAffiliateGroupsResponse {
	count: number;
	items: AffiliateGroupRow[];
}

export interface CreateAffiliateGroupBody {
	name: string;
	color?: string | null;
	description?: string | null;
}

export interface AffiliateGroupDetail extends AffiliateGroupRow {
	members: string[];
}

export interface AssignMembershipBody {
	affiliate_id: string;
}

export interface AssignResponse {
	group_id: string;
	affiliate_id: string;
	tenant_id: string;
	added_at: number;
}

export interface UnassignResponse {
	group_id: string;
	affiliate_id: string;
	removed: boolean;
}

const BASE = "/api/v1/affiliate-groups";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListAffiliateGroupsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAffiliateGroupsResponse>(res);
}

export async function getOne(id: string): Promise<AffiliateGroupDetail> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<AffiliateGroupDetail>(res);
}

export async function create(
	body: CreateAffiliateGroupBody,
): Promise<AffiliateGroupRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<AffiliateGroupRow>(res);
}

export async function assign(
	groupId: string,
	body: AssignMembershipBody,
): Promise<AssignResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(groupId)}/assign`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<AssignResponse>(res);
}

export async function unassign(
	groupId: string,
	body: AssignMembershipBody,
): Promise<UnassignResponse> {
	const res = await fetch(
		`${BASE}/${encodeURIComponent(groupId)}/unassign`,
		{
			method: "POST",
			credentials: "include",
			headers: {
				accept: "application/json",
				"content-type": "application/json",
			},
			body: JSON.stringify(body),
		},
	);
	return parseOrThrow<UnassignResponse>(res);
}

export const affiliateGroupsQueryKeys = {
	all: ["affiliate-groups"] as const,
	list: () => [...affiliateGroupsQueryKeys.all] as const,
	detail: (id: string) => [...affiliateGroupsQueryKeys.all, id] as const,
};
